import { createFileRoute } from '@tanstack/react-router';
import { formatDate, todayInDhaka } from '@thrivehrm/shared';
import { PageHeader } from '@/components/page-header';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { greeting } from '@/features/dashboard/greeting';
import { useAuth } from '@/lib/auth';
import { appModules } from '@/modules/core';
import { type DashboardWidget, visibleWidgets } from '@/modules/registry';

export const Route = createFileRoute('/_app/')({
  component: DashboardPage,
});

/**
 * Widgets that ask something of the user go in the wide column, in this order (design
 * `A00-home`: "Waiting on you" first); every other widget — counts, statuses, balances — goes in
 * the "At a glance" column in registry order. Layout only: which widgets show is still decided by
 * `visibleWidgets` alone.
 */
const MAIN_COLUMN: readonly string[] = [
  'approvals',
  'attendance-today',
  'my-profile',
  'movements',
  'data-gaps',
];

function WidgetCard({ widget, raised }: { widget: DashboardWidget; raised: boolean }) {
  const { id, title, component: Widget } = widget;
  return (
    <Card
      aria-labelledby={`widget-${id}`}
      variant={raised ? 'raised' : 'default'}
      data-widget={id}
      className="min-w-0"
    >
      <CardHeader title={<span id={`widget-${id}`}>{title}</span>} />
      <CardContent className="pt-4">
        <Widget />
      </CardContent>
    </Card>
  );
}

/** Role-aware dashboard: cards come from the module widget registry (EXT-10, FR-DASH). */
function DashboardPage() {
  const { user } = useAuth();
  const widgets = visibleWidgets(appModules, user);
  const firstName = user?.name.split(' ')[0] ?? '';
  const main = MAIN_COLUMN.flatMap((id) => widgets.filter((w) => w.id === id));
  const side = widgets.filter((w) => !MAIN_COLUMN.includes(w.id));
  const twoColumns = main.length > 0 && side.length > 0;

  return (
    <>
      <PageHeader
        title={greeting(firstName)}
        description={`Today is ${formatDate(todayInDhaka())}.`}
      />
      <div
        className={
          twoColumns
            ? 'grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,380px)]'
            : 'grid gap-6'
        }
      >
        {main.length > 0 ? (
          <div className="flex min-w-0 flex-col gap-6">
            {main.map((w, i) => (
              <WidgetCard key={w.id} widget={w} raised={i === 0} />
            ))}
          </div>
        ) : null}
        {side.length > 0 ? (
          <aside
            aria-label="At a glance"
            className={
              twoColumns ? 'flex min-w-0 flex-col gap-4' : 'grid items-start gap-4 md:grid-cols-2'
            }
          >
            {side.map((w, i) => (
              <WidgetCard key={w.id} widget={w} raised={main.length === 0 && i === 0} />
            ))}
          </aside>
        ) : null}
      </div>
    </>
  );
}
